import { getSampleTypeById } from '../config/drumSamples';
import { getDrumPattern, getRecommendedDrumKit, applyPatternToGrid } from '../config/drumPatterns';
import { getDrumKit } from '../config/drumKits';
import * as Tone from 'tone';
import { TIME_SIGNATURES } from '../config/timeSignatures';
import { DEFAULT_INSTRUMENTS } from '../config/defaultInstruments';
import { saveAs } from 'file-saver';
import { devLog } from '../utils/devLog';
import { getSafeAudioUrl } from '../utils/audioUtils';
import { calcStepsPerBar } from '../utils/steps';
import type { 
  DrumInstrument, 
  DrumGridData, 
  TimeSignature, 
  NoteDivision,
  DrumsetConfig 
} from '../types';
import React from 'react';

// Fix grid length to match stepsPerBar
const fixGridLength = (grid: DrumGridData, stepsPerBar: number): DrumGridData => {
  const newGrid: DrumGridData = {};
  Object.keys(grid).forEach(id => {
    const arr = grid[id] || [];
    if (arr.length > stepsPerBar) {
      newGrid[id] = arr.slice(0, stepsPerBar);
    } else if (arr.length < stepsPerBar) {
      newGrid[id] = [...arr, ...Array(stepsPerBar - arr.length).fill(null)];
    } else {
      newGrid[id] = arr;
    }
  });
  return newGrid;
};

interface UsePatternIOOptions {
  instruments: DrumInstrument[];
  setInstruments: (instruments: DrumInstrument[]) => void;
  grid: DrumGridData;
  setGrid: (grid: DrumGridData | ((prev: DrumGridData) => DrumGridData)) => void;
  timeSignature: TimeSignature;
  setTimeSignature: (sig: TimeSignature) => void;
  noteDivision: NoteDivision;
  setNoteDivision: (div: NoteDivision) => void;
  loopCount: number;
  setLoopCount: (count: number) => void;
  tempo: number;
  setTempo: (tempo: number) => void;
  stepsPerBar: number;
  setStepsPerBar?: (steps: number) => void;
  isPlaying: boolean;
  setIsPlaying: (playing: boolean) => void;
  setIsPaused: (paused: boolean) => void;
  resetStep: () => void;
  stopPlayback?: () => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onDrumsetUpdate?: (config: DrumsetConfig) => void;
  showDynamicMessage?: (message: string, type?: "info" | "success" | "warning" | "error", duration?: number) => void;
  t?: (key: string) => string;
  isImportingRef?: React.MutableRefObject<boolean>;
  setIsImporting: (importing: boolean) => void;
  lastDrumsetConfigRef?: React.MutableRefObject<DrumsetConfig | null>;
  setSelectedPattern?: (pattern: string) => void;
  setIsGridModified?: (modified: boolean) => void;
  drumsetConfig?: DrumsetConfig | null;
}

interface UsePatternIOReturn {
  exportPattern: () => void;
  importPattern: (event: React.ChangeEvent<HTMLInputElement>) => void;
  loadPresetPattern: (patternKey: string) => Promise<void>;
  triggerImport: () => void;
}

interface PatternData {
  version: string;
  timestamp: string;
  settings: {
    timeSignature: TimeSignature;
    noteDivision: NoteDivision;
    loopCount: number;
    tempo: number;
    stepsPerBar: number;
  };
  drumset: {
    instruments: Array<{
      id: string;
      name: string;
      type: string;
      sampleId: string;
      color: string;
    }>;
    name?: string; // 自定義鼓組名稱
  };
  grid: DrumGridData;
  metadata: {
    totalSteps: number;
    instrumentCount: number;
  };
}

interface ImportedData {
  type?: string;
  version?: string;
  settings?: {
    timeSignature?: TimeSignature;
    noteDivision?: NoteDivision;
    loopCount?: number;
    tempo?: number;
  };
  drumset?: {
    instruments?: Array<{
      id: string;
      name: string;
      type?: string;
      sampleId?: string;
      sample?: string;
      color: string;
    }>;
    name?: string; // 自定義鼓組名稱
  };
  grid?: DrumGridData;
  instruments?: Array<{
    id: string;
    name: string;
    type?: string;
    sampleId?: string;
    sample?: string;
    color: string;
  }>;
}

export default function usePatternIO(options: UsePatternIOOptions): UsePatternIOReturn {
  const {
    instruments,
    setInstruments,
    grid,
    setGrid,
    timeSignature,
    setTimeSignature,
    noteDivision,
    setNoteDivision,
    loopCount,
    setLoopCount,
    tempo,
    setTempo,
    stepsPerBar,
    setStepsPerBar,
    isPlaying,
    setIsPlaying,
    setIsPaused,
    resetStep,
    stopPlayback,
    fileInputRef,
    onDrumsetUpdate,
    showDynamicMessage,
    t,
    isImportingRef,
    setIsImporting,
    lastDrumsetConfigRef,
    setSelectedPattern,
    setIsGridModified,
    drumsetConfig
  } = options;

  const exportPattern = (): void => {
    const patternData: PatternData = {
      version: '2.0',
      timestamp: new Date().toISOString(),
      settings: { timeSignature, noteDivision, loopCount, tempo, stepsPerBar },
      drumset: {
        instruments: instruments.map(({ id, name, type, color, sampleId, sample }) => ({
          id,
          name,
          type,
          sampleId: sampleId || sample || 'kick-classic',
          color
        })),
        name: drumsetConfig?.name // 包含自定義鼓組名稱
      },
      grid,
      metadata: { totalSteps: stepsPerBar, instrumentCount: instruments.length }
    };

    const customName = drumsetConfig?.name;
    const baseFilename = customName 
      ? `drum-pattern-${customName.replace(/[^a-zA-Z0-9]/g, '-')}`
      : 'drum-pattern';
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const filename = `${baseFilename}-${timestamp}.json`;
    
    const dataStr = JSON.stringify(patternData, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json;charset=utf-8' });
    saveAs(blob, filename);
  };

  const importPattern = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const result = e.target?.result;
        if (typeof result !== 'string') return;
        
        const data: ImportedData = JSON.parse(result);

        if (data.type === 'drumset') {
          setIsImporting(true);
          if (data.instruments) {
            const safeInstruments = data.instruments.map(inst => {
              const sampleId = inst.sampleId || inst.sample || 'kick-classic';
              const sampleType = inst.type || getSampleTypeById(sampleId) || 'percussion';
              return {
                id: inst.id,
                name: inst.name,
                type: sampleType,
                url: getSafeAudioUrl(sampleId),
                color: inst.color,
                sampleId
              };
            });
            if (lastDrumsetConfigRef) {
              lastDrumsetConfigRef.current = { instruments: safeInstruments };
            }
            onDrumsetUpdate && onDrumsetUpdate({ instruments: safeInstruments });
            setInstruments(safeInstruments);
            setTimeout(() => { setIsImporting(false); }, 2000);
          }
        } else if (data.version && data.settings && data.grid) {
          setIsImporting(true);
          
          // 先從匯入檔案中獲取設定值，並使用正確的值計算stepsPerBar
          const importedTimeSignature = data.settings.timeSignature || timeSignature;
          const importedNoteDivision = data.settings.noteDivision || noteDivision;
          const importedLoopCount = data.settings.loopCount || loopCount;
          const importedTempo = data.settings.tempo || tempo;
          
          // 立即設置新的設定值
          setTimeSignature(importedTimeSignature);
          setNoteDivision(importedNoteDivision);
          setLoopCount(importedLoopCount);
          setTempo(importedTempo);

          if (data.drumset && data.drumset.instruments) {
            const safeInstruments = data.drumset.instruments.map(inst => {
              const sampleId = inst.sampleId || inst.sample || 'kick-classic';
              const sampleType = inst.type || getSampleTypeById(sampleId) || 'percussion';
              return {
                id: inst.id,
                name: inst.name,
                type: sampleType,
                url: getSafeAudioUrl(sampleId),
                color: inst.color,
                sampleId
              };
            });
            
            // 包含drumset name的完整配置
            const drumsetConfig = { 
              instruments: safeInstruments,
              name: data.drumset.name // 保留自定義鼓組名稱
            };
            
            if (lastDrumsetConfigRef) {
              lastDrumsetConfigRef.current = drumsetConfig;
            }
            onDrumsetUpdate && onDrumsetUpdate(drumsetConfig);
            localStorage.setItem('drumsetConfig', JSON.stringify(drumsetConfig));
            setInstruments(safeInstruments);
          }

          if (data.grid) {
            // 使用匯入檔案中的timeSignature和noteDivision來計算正確的stepsPerBar
            const importedStepsPerBar = calcStepsPerBar(importedTimeSignature, importedNoteDivision);
            
            setTimeout(() => {
              setGrid(fixGridLength(data.grid!, importedStepsPerBar));
              // 導入後重置grid修改狀態
              if (setIsGridModified) {
                setIsGridModified(false);
              }
              setTimeout(() => { setIsImporting(false); }, 2000);
            }, 200);
          } else {
            setTimeout(() => { setIsImporting(false); }, 2000);
          }
        } else {
          showDynamicMessage && t && showDynamicMessage(t('invalidFileFormat'), 'error');
          // 確保在錯誤情況下也重置importing狀態
          setIsImporting(false);
          return;
        }

        if (isPlaying) {
          setIsPlaying(false);
          setIsPaused(false);
          resetStep();
          Tone.Transport.stop();
          Tone.Transport.cancel();
        }
      } catch (error) {
        console.error('Error importing file:', error);
        showDynamicMessage && t && showDynamicMessage(t('errorReadingFile'), 'error');
        const defaultWithUrls = DEFAULT_INSTRUMENTS.map(inst => ({ ...inst, url: getSafeAudioUrl(inst.sampleId) }));
        setInstruments(defaultWithUrls);
        // 確保在錯誤情況下也重置importing狀態
        setIsImporting(false);
      }
    };

    reader.readAsText(file);
    event.target.value = '';
  };

  const triggerImport = (): void => {
    fileInputRef?.current?.click();
  };

  const loadPresetPattern = async (patternKey: string): Promise<void> => {
    const pattern = getDrumPattern(patternKey);
    if (!pattern) return;

    if (isPlaying && stopPlayback) stopPlayback();

    // 設置importing狀態，無論是否有推薦鼓組
    setIsImporting(true);

    const recommendedDrumKitKey = getRecommendedDrumKit(patternKey);
    let targetInstruments = instruments;

    if (recommendedDrumKitKey) {
      const recommendedDrumKit = getDrumKit(recommendedDrumKitKey);
      if (recommendedDrumKit) {
        const safeInstruments = recommendedDrumKit.instruments.map((inst: DrumInstrument) => ({
          ...inst,
          url: getSafeAudioUrl(inst.sampleId)
        }));
        setInstruments(safeInstruments);
        targetInstruments = safeInstruments;
        onDrumsetUpdate && onDrumsetUpdate({ instruments: safeInstruments });
        localStorage.setItem('drumsetConfig', JSON.stringify({ instruments: safeInstruments }));
      }
    }

    setTimeSignature(pattern.timeSignature as TimeSignature);
    setNoteDivision(pattern.noteDivision as NoteDivision);
    setTempo(pattern.tempo);

    const newStepsPerBar = calcStepsPerBar(pattern.timeSignature as TimeSignature, pattern.noteDivision as NoteDivision);
    const patternGrid = applyPatternToGrid(pattern, targetInstruments) as DrumGridData;
    const newGrid = fixGridLength(patternGrid, newStepsPerBar);

    requestAnimationFrame(() => {
      setGrid(newGrid);
      // 重要：重置步數到正確的基礎步數，這會觸發DrumRNNButton重置倍數為1x
      if (setStepsPerBar && newStepsPerBar !== stepsPerBar) {
        setStepsPerBar(newStepsPerBar);
        devLog(`🔄 Pattern Load: Reset stepsPerBar from ${stepsPerBar} to ${newStepsPerBar}`);
      }
      resetStep();
      // 確保在所有操作完成後重置importing狀態
      setTimeout(() => { 
        setIsImporting(false); 
      }, 500);
    });
    
    // 設置selectedPattern以便UI同步，並重置grid修改狀態
    if (setSelectedPattern) {
      setSelectedPattern(patternKey);
    }
    if (setIsGridModified) {
      setIsGridModified(false);
    }
  };

  return { exportPattern, importPattern, loadPresetPattern, triggerImport };
} 